import React, { useEffect, useState } from 'react';
import { Loader } from '@/components/custom/loader';
import { useSession } from '@/hooks/useSession';
import { DashboardSummaryCard } from './dashboard-summary-card';
import { DashboardQuickAccess } from './dashboard-quick-access';
import { FiEdit3, FiMap, FiMessageSquare, FiMonitor, FiUsers } from 'react-icons/fi';
import { BsFileEarmarkCheck } from 'react-icons/bs';
import IconPerformance from '@/app/components/Icons/IconPerformance';
import { VscGame } from 'react-icons/vsc';
import { useNotification } from '@/hooks/useNotification';
import { EmptyState } from '@/components/custom/empty-state';
import { useTranslation } from 'react-i18next';
import { NotificationsMessageItem } from '@/components/custom/notifications-message';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { WarningWall, WarningWallSlider } from '@/components/custom/warning-wall';
import { DashboardSection, DashboardSectionTitle, DashboardSummaryWrapper } from '@/components/custom/dashboard';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { NotificationsBody } from '@/components/custom/notifications/notifications';

export const DashboardTeacher = () => {
  const [loading, setLoading] = useState(false);
  const { session } = useSession();
  const { t } = useTranslation();
  const { notificationsComment, setNotificationsComment, notificationCommentCount, setNotificationCommentCount } = useNotification();
  const client = useClient();
  const [pendingCorrections, setPendingCorrections] = useState();
  const [banners, setBanners] = useState();
  const { abilities } = usePlatformAbilities();

  const getPendingCorrections = async () => {
    const { data, error } = await client.fetch('/dashboards/cards');

    if (error) {
      toast.error('Erro ao buscar correções pendentes');
    } else {
      setPendingCorrections(data);
    }
  };

  const fetchBanners = () => {
    setLoading(true);
    const fetchBanners = client.fetch(`/bulletin_boards/active`);

    let promises = [fetchBanners];

    Promise.allSettled(promises).then(([bannersResponse]) => {
      if (bannersResponse?.value?.error) {
        toast.error('Erro ao buscar banners');
      } else {
        setBanners(bannersResponse?.value?.data);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    getPendingCorrections();
    fetchBanners();
  }, []);

  const unreadNotifications = notificationsComment?.filter(item => item.unread === true);

  const breadcrumbs = {
    title: 'Início',
    nav: [
      {
        route: '/',
        name: `Olá, ${session?.user?.name}`,
        isActive: true
      }
    ]
  };

  const shortcuts = [
    {
      title: 'Monitoria',
      link: '/auditorio',
      icon: (
        <FiMessageSquare
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      available: abilities?.auditorio
    },
    {
      title: 'Redação',
      icon: (
        <FiEdit3
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/redacao',
      available: abilities?.redacao
    },
    {
      title: 'Atividades',
      icon: (
        <BsFileEarmarkCheck
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/atividade-complementar',
      available: abilities?.['cursos-ativ-complementar']
    },
    {
      title: 'Cursos',
      icon: (
        <FiMonitor
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/curso/admin',
      available: abilities?.cursos
    },
    {
      title: 'Plano de aula',
      icon: (
        <FiMap
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/plano-de-aulas/inicio'
    },
    {
      title: 'Minhas turmas',
      icon: (
        <FiUsers
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/turmas',
      available: abilities?.turmas
    },
    {
      title: 'Desafio da semana',
      icon: (
        <VscGame
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/desafio-da-semana/desafios',
      available: abilities?.['desafio-da-semana']
    },
    {
      title: 'Relatórios',
      icon: (
        <IconPerformance
          size={20}
          style={{ maxWidth: 90 }}
        />
      ),
      link: '/metricas/institucional',
      available: abilities?.metricas
    }
  ];

  return loading ? (
    <Loader />
  ) : (
    <>
      <Breadcrumbs data={breadcrumbs} />

      {loading ? (
        <Loader />
      ) : banners?.length > 1 && !loading ? (
        <WarningWallSlider data={banners} />
      ) : (
        !loading && (
          <WarningWall
            loading={loading}
            warning={banners?.[0]}
            key={banners?.[0]?.id}
          />
        )
      )}

      <DashboardSection>
        <DashboardSectionTitle>Para corrigir</DashboardSectionTitle>

        <DashboardSummaryWrapper className="mb-0">
          {abilities?.redacao && (
            <DashboardSummaryCard
              title="Redações para revisar"
              count={pendingCorrections?.['surveys_count']}
              link="Acessar redações"
              route="/redacao"
            />
          )}
          {abilities?.projetos && (
            <DashboardSummaryCard
              title="Projetos (versões para avaliar)"
              count={pendingCorrections?.['project_versions_count']}
              link="Acessar projetos"
              route="/projetos/grupos"
            />
          )}
          {abilities?.['cursos-ativ-complementar'] && (
            <DashboardSummaryCard
              title="Atividades para avaliar"
              count={pendingCorrections?.['activities_count']}
              link="Acessar atividades"
              route="/atividade-complementar"
            />
          )}
        </DashboardSummaryWrapper>
      </DashboardSection>

      <DashboardSection>
        <DashboardSectionTitle>Acessos rápidos</DashboardSectionTitle>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(174px,1fr))] gap-3">
          {shortcuts
            ?.filter(item => item.available)
            ?.map(item => (
              <DashboardQuickAccess
                title={item.title}
                icon={item.icon}
                link={item.link}
              />
            ))}
        </div>
      </DashboardSection>

      <DashboardSection>
        <DashboardSectionTitle>Mensagens</DashboardSectionTitle>

        {unreadNotifications.length === 0 && (
          <EmptyState
            type="notification"
            title={t('emptyState.notificationTitle')}
          />
        )}

        {unreadNotifications?.map(notification => (
          <NotificationsBody variant="dashboard">
            <NotificationsMessageItem
              notifications={notificationsComment}
              notification={notification}
              setNotificationCount={setNotificationCommentCount}
              setNotifications={setNotificationsComment}
              notificationCount={notificationCommentCount}
              key={notification.id}
            />
          </NotificationsBody>
        ))}
      </DashboardSection>
    </>
  );
};
